import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, Mail, Send, Loader } from 'lucide-react';
import './SendingSettings.css';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { apiClient } from '../../services/api';

interface SMTPConfig {
    smtp_server: string;
    smtp_port: number;
    smtp_user: string;
    smtp_password: string;
    smtp_security: string;
    from_name: string;
    from_address: string;
    reply_to: string;
    verified: boolean;
    is_active: boolean;
}

const EMPTY_CONFIG: SMTPConfig = {
    smtp_server: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_security: 'tls',
    from_name: '',
    from_address: '',
    reply_to: '',
    verified: false,
    is_active: true,
};

export default function SendingEmailSettings() {
    const [config, setConfig] = useState<SMTPConfig>(EMPTY_CONFIG);
    const [hasConfig, setHasConfig] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        setLoading(true);
        try {
            const res = await apiClient.get('/users/me/suitecrm/email-config');
            if (res.data) {
                setConfig({ ...EMPTY_CONFIG, ...res.data, smtp_password: '' });
                setHasConfig(true);
            }
        } catch {
            setHasConfig(false);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSaved(false);
        try {
            await apiClient.post('/users/me/suitecrm/email-config', config);
            setHasConfig(true);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
            await loadConfig();
        } catch (e: any) {
            setError(e.response?.data?.detail || 'Failed to save configuration');
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const res = await apiClient.post('/users/me/suitecrm/email-config/test');
            setTestResult(res.data);
            if (res.data.success) {
                await loadConfig();
            }
        } catch (e: any) {
            setTestResult({ success: false, message: e.response?.data?.detail || 'Test failed' });
        } finally {
            setTesting(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Remove your sending email configuration? Sequences will fall back to the platform default address.')) return;
        try {
            await apiClient.delete('/users/me/suitecrm/email-config');
            setConfig(EMPTY_CONFIG);
            setHasConfig(false);
        } catch (e: any) {
            setError(e.response?.data?.detail || 'Failed to remove configuration');
        }
    };

    const field = (label: string, key: keyof SMTPConfig, type = 'text', placeholder = '') => (
        <div className="sending-field">
            <label className="sending-label">{label}</label>
            <input
                className="sending-input"
                type={type}
                value={config[key] as string}
                placeholder={placeholder}
                onChange={e => setConfig(c => ({ ...c, [key]: e.target.value }))}
            />
        </div>
    );

    if (loading) return (
        <div className="sending-settings">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)' }}>
                <Loader size={16} className="spin" /> Loading…
            </div>
        </div>
    );

    return (
        <div className="sending-settings">
            <div className="sending-header">
                <Mail size={20} style={{ color: 'var(--primary)' }} />
                <div>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>Sending Email</h2>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                        Configure your own SMTP server so sequences send from your domain.
                        This significantly improves deliverability and inbox placement.
                    </p>
                </div>
            </div>

            {config.verified && (
                <div className="sending-verified-banner">
                    <CheckCircle size={16} />
                    <span>Verified — sequences send from <strong>{config.from_address || config.smtp_user}</strong></span>
                </div>
            )}

            {!config.verified && hasConfig && (
                <div className="sending-warn-banner">
                    <AlertTriangle size={16} />
                    <span>SMTP configured but not yet verified. Send a test email to confirm it works.</span>
                </div>
            )}

            {!hasConfig && (
                <div className="sending-warn-banner">
                    <AlertTriangle size={16} />
                    <span>No custom sending email configured. Sequences will send from the platform default address, which may affect deliverability.</span>
                </div>
            )}

            <GlassCard>
                <div className="sending-form">
                    <div className="sending-row">
                        {field('SMTP Server', 'smtp_server', 'text', 'mail.yourdomain.com')}
                        <div className="sending-field sending-field-sm">
                            <label className="sending-label">Port</label>
                            <input
                                className="sending-input"
                                type="number"
                                value={config.smtp_port}
                                onChange={e => setConfig(c => ({ ...c, smtp_port: parseInt(e.target.value) || 587 }))}
                            />
                        </div>
                        <div className="sending-field sending-field-sm">
                            <label className="sending-label">Security</label>
                            <select
                                className="sending-input"
                                value={config.smtp_security}
                                onChange={e => setConfig(c => ({ ...c, smtp_security: e.target.value }))}
                            >
                                <option value="tls">TLS (STARTTLS)</option>
                                <option value="ssl">SSL</option>
                                <option value="none">None</option>
                            </select>
                        </div>
                    </div>

                    <div className="sending-row">
                        {field('Username / Email', 'smtp_user', 'email', 'you@yourdomain.com')}
                        {field('Password', 'smtp_password', 'password', hasConfig ? '••••••••' : '')}
                    </div>

                    <div className="sending-row">
                        {field('From Name', 'from_name', 'text', 'Your Company')}
                        {field('From Address', 'from_address', 'email', 'hello@yourdomain.com')}
                    </div>

                    {field('Reply-To (optional)', 'reply_to', 'email', 'replies@yourdomain.com')}
                </div>
            </GlassCard>

            {error && (
                <div className="sending-error">
                    <AlertTriangle size={14} /> {error}
                </div>
            )}

            {testResult && (
                <div className={testResult.success ? 'sending-verified-banner' : 'sending-error'}>
                    {testResult.success ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
                    {testResult.message}
                </div>
            )}

            <div className="sending-actions">
                <GlassButton variant="primary" onClick={handleSave} disabled={saving}>
                    {saving ? <Loader size={14} className="spin" /> : null}
                    {saved ? 'Saved!' : saving ? 'Saving…' : 'Save Configuration'}
                </GlassButton>

                {hasConfig && (
                    <GlassButton variant="secondary" onClick={handleTest} disabled={testing}>
                        {testing ? <Loader size={14} className="spin" /> : <Send size={14} />}
                        {testing ? 'Sending test…' : 'Send Test Email'}
                    </GlassButton>
                )}

                {hasConfig && (
                    <GlassButton variant="danger" onClick={handleDelete}>
                        Remove
                    </GlassButton>
                )}
            </div>

            <div className="sending-dns-hint">
                <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                    Recommended DNS records for best deliverability
                </h4>
                <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--text-secondary)' }}>
                    Add these to your domain's DNS if not already present. Your hosting provider can help.
                </p>
                <code className="sending-dns-code">
                    TXT @ "v=spf1 include:{config.smtp_server || 'mail.yourdomain.com'} ~all"
                </code>
                <code className="sending-dns-code">
                    TXT _dmarc "v=DMARC1; p=none; rua=mailto:dmarc@{config.from_address?.split('@')[1] || 'yourdomain.com'}"
                </code>
            </div>
        </div>
    );
}
